import { ApolloClient, FetchResult } from '@apollo/client';
import axios, {
  AxiosInstance,
  AxiosProgressEvent,
  AxiosRequestConfig,
  RawAxiosRequestConfig,
} from 'axios';
import axiosRetry from 'axios-retry';
import mime from 'mime/lite';

import {
  abortMultipartUpload,
  abortMultipartUploadVariables,
} from '../../../../src/graphql/athena/mutations/__generated__/abortMultipartUpload';
import {
  completeMultipartUpload,
  completeMultipartUploadVariables,
} from '../../../../src/graphql/athena/mutations/__generated__/completeMultipartUpload';
import {
  generateMultipartUploadUrls,
  generateMultipartUploadUrlsVariables,
  generateMultipartUploadUrls_multipartUploads_generateMultipartUploadUrls_multipartUploadUrls as MultipartUploadUrls,
} from '../../../../src/graphql/athena/mutations/__generated__/generateMultipartUploadUrls';
import {
  initiateMultipartUpload,
  initiateMultipartUploadVariables,
} from '../../../../src/graphql/athena/mutations/__generated__/initiateMultipartUpload';
import {
  s3DeleteDocument as s3DeleteDocumentMutation,
  s3DeleteDocumentVariables,
} from '../../../../src/graphql/athena/mutations/__generated__/s3DeleteDocument';
import {
  AbortMultipartUploadMutation,
  CompleteMultipartUploadMutation,
  GenerateMultipartUploadUrlsMutation,
  InitiateMultipartUploadMutation,
  S3DeleteDocumentMutation,
} from '../../../../src/graphql/athena/mutations/S3DocumentUpload';
import {
  getS3DocumentDownloadUrl as getS3DocumentDownloadUrlQuery,
  getS3DocumentDownloadUrlVariables,
} from '../../../../src/graphql/athena/queries/__generated__/getS3DocumentDownloadUrl';
import {
  getS3DocumentUploadUrl,
  getS3DocumentUploadUrlVariables,
} from '../../../../src/graphql/athena/queries/__generated__/getS3DocumentUploadUrl';
import {
  GetS3DocumentDownloadUrlQuery,
  GetS3DocumentUploadUrlQuery,
} from '../../../../src/graphql/athena/queries/S3DocumentDetails';
import { ADMIN_CLIENT_UPLOAD_BUCKET } from '../../../../src/helpers/globals';
import { OdProtosWebServicesMultipartUploads_MultipartUploadPartInput as MultipartUploadPart } from '../../../__generated__/athenaGlobalTypes';

export enum S3UploadDestination {
  SSVA, // Self serve virtual assessment
  DCO, // digital checkout
  AGENT_CONTRACT, // Agent contract uploads
  RESALE_OFFER_PORTAL, /// Buy Direct pre-approval document upload
}

export const clientUploadBucketName = ADMIN_CLIENT_UPLOAD_BUCKET;

function getS3Directory(identifier: string, destination: S3UploadDestination) {
  switch (destination) {
    case S3UploadDestination.SSVA:
      return `uploads/Cake/SellerInput-${identifier}/`;
    case S3UploadDestination.DCO:
      return `uploads/photos/Lead/${identifier}/digital_checkout_photos/`;
    case S3UploadDestination.AGENT_CONTRACT:
      return `uploads/Offer/${identifier}`;
    case S3UploadDestination.RESALE_OFFER_PORTAL:
      return `uploads/ResaleOfferPortal/${identifier}/`;
  }
}
/**
 * This helper function generates a unique s3 key for a file upload.
 */
export function generateS3Key(file: File, identifier: string, destination: S3UploadDestination) {
  const fileType = mime.getType(file.name);
  const fileNameParts = file.name.split('.');
  const fileExt = fileType ? mime.getExtension(fileType) : 'error';

  const fileName = fileType ? fileNameParts.slice(0, -1).join('-') : fileNameParts.join('-');

  const fileNameKey = sanitizeS3Key(`${fileName}-${new Date().toISOString()}`);
  const filePath = `${fileNameKey}.${fileExt}`;
  const directory = getS3Directory(identifier, destination);

  return `${directory}${filePath}`;
}

interface RetryOptions {
  /** Max number of retries to attempt. */
  retries: number;
  /** The delay formula is `Math.pow(2, {retryCount}) * {delayMultiplier})`. */
  delayMultiplier: number;
}

/**
 * @description
 * helper method which returns an axios instance with retries
 */
export function getAxiosInstance(
  { retries, delayMultiplier }: RetryOptions = { retries: 3, delayMultiplier: 1000 },
): AxiosInstance {
  const axiosInstance: AxiosInstance = axios.create();
  delete axios.defaults.headers.put['Content-Type'];
  axiosRetry(axiosInstance, {
    retries: retries,
    retryDelay: (retryCount) => {
      return Math.pow(2, retryCount) * delayMultiplier;
    },
  });
  return axiosInstance;
}

function sanitizeS3Key(key: string): string {
  // https://docs.aws.amazon.com/AmazonS3/latest/dev/BucketRestrictions.html
  return key && key.replace(/[^0-9a-zA-Z!\-']/g, '-').toLowerCase();
}

/**
 * @description
 * helper method which returns wrap's the GetS3DocumentUploadUrlQuery
 */
export const getS3DocumentUrl = async (
  apolloClient: ApolloClient<unknown>,
  bucketName: string,
  documentName: string,
) => {
  return await apolloClient.query<getS3DocumentUploadUrl, getS3DocumentUploadUrlVariables>({
    query: GetS3DocumentUploadUrlQuery,
    variables: {
      bucketName: bucketName,
      documentName: documentName,
    },
    context: {
      clientName: 'athena',
    },
    fetchPolicy: 'cache-first',
  });
};

/**
 * helper method which returns wrap's the GetS3DocumentDownloadUrlQuery
 */
export const getS3DocumentDownloadUrl = async (
  apolloClient: ApolloClient<unknown>,
  bucketName: string,
  s3Directory: string,
  ttlSeconds?: number,
) => {
  return apolloClient.query<getS3DocumentDownloadUrlQuery, getS3DocumentDownloadUrlVariables>({
    query: GetS3DocumentDownloadUrlQuery,
    variables: {
      bucketName,
      s3Directory,
      ttlSeconds,
    },
    context: {
      linkContext: {
        team: 'seller-engineering',
      },
    },
  });
};

/**
 * helper method which returns wrap's the S3DeleteDocumentMutation
 */
export const s3DeleteDocument = async (
  apolloClient: ApolloClient<unknown>,
  bucketName: string,
  documentName: string,
) => {
  return apolloClient.mutate<s3DeleteDocumentMutation, s3DeleteDocumentVariables>({
    mutation: S3DeleteDocumentMutation,
    variables: {
      bucketName,
      documentName,
    },
    fetchPolicy: 'no-cache',
    context: {
      clientName: 'athena',
    },
  });
};

/**
 * @description
 * helper method which returns wrap's the InitiateMultipartUploadMutation
 */
export const initiateUpload = async (
  apolloClient: ApolloClient<unknown>,
  bucketName: string,
  filePath: string,
  contentType: string,
): Promise<FetchResult<initiateMultipartUpload>> => {
  return await apolloClient.mutate<initiateMultipartUpload, initiateMultipartUploadVariables>({
    mutation: InitiateMultipartUploadMutation,
    variables: {
      bucketName: bucketName,
      filePath: filePath,
      contentType: contentType,
    },
    fetchPolicy: 'no-cache',
    context: {
      clientName: 'athena',
    },
  });
};

/**
 * @description
 * helper method which returns wrap's the GenerateMultipartUploadUrlsMutation
 */
export const generateUploadUrls = async (
  apolloClient: ApolloClient<unknown>,
  uploadToken: string,
  partsCount: number,
): Promise<FetchResult<generateMultipartUploadUrls>> => {
  return await apolloClient.mutate<
    generateMultipartUploadUrls,
    generateMultipartUploadUrlsVariables
  >({
    mutation: GenerateMultipartUploadUrlsMutation,
    variables: {
      uploadToken: uploadToken,
      partIndexStart: 1,
      partIndexEnd: partsCount,
    },
    fetchPolicy: 'no-cache',
    context: {
      clientName: 'athena',
    },
  });
};

/**
 * @description
 * helper method which returns wrap's the CompleteMultipartUploadMutation
 */
export const completeUpload = async (
  apolloClient: ApolloClient<unknown>,
  uploadToken: string,
  multipartUploadParts: MultipartUploadPart[],
): Promise<FetchResult<completeMultipartUpload>> => {
  return await apolloClient.mutate<completeMultipartUpload, completeMultipartUploadVariables>({
    mutation: CompleteMultipartUploadMutation,
    variables: {
      uploadToken: uploadToken,
      multipartUploadParts: multipartUploadParts,
    },
    fetchPolicy: 'no-cache',
    context: {
      clientName: 'athena',
    },
  });
};

/**
 * @description
 * helper method which returns wrap's the AbortMultipartUploadMutation
 */
export const abortUpload = async (
  apolloClient: ApolloClient<unknown>,
  uploadToken: string,
): Promise<FetchResult<abortMultipartUpload>> => {
  return await apolloClient.mutate<abortMultipartUpload, abortMultipartUploadVariables>({
    mutation: AbortMultipartUploadMutation,
    variables: {
      uploadToken: uploadToken,
    },
    fetchPolicy: 'no-cache',
    context: {
      clientName: 'athena',
    },
  });
};

export type FileUpload = {
  file: File;
  s3Key: string;
  progress: number;
  uploadLatency: number;
  multipartUpload?: boolean;
  completed: boolean;
  errorOccurred: boolean;
  errorMessage: string;
  emitMetrics?: boolean;
  downloadUrl?: string;
};

const singlePartUpload = async (
  fileUpload: FileUpload,
  apolloClient: ApolloClient<unknown>,
  axiosInstance: AxiosInstance,
  axiosConfig: AxiosRequestConfig,
  onUpdate: (fileUpload: FileUpload) => void,
) => {
  const uploadStartTime = Date.now();

  const getS3UrlResult = await getS3DocumentUrl(
    apolloClient,
    ADMIN_CLIENT_UPLOAD_BUCKET,
    fileUpload.s3Key ?? '',
  );

  const s3DocumentUploadUrl = getS3UrlResult.data?.seller.s3DocumentUploadUrl;
  if (!s3DocumentUploadUrl) {
    onUpdate({
      ...fileUpload,
      emitMetrics: true,
      errorOccurred: true,
      errorMessage: `Single part upload error, failed to fetch s3 Url from getS3DocumentUploadUrl`,
    });
    return;
  }

  await axiosInstance
    .put(s3DocumentUploadUrl, fileUpload.file, axiosConfig)
    .then(() => {
      const elapsedMillis = Date.now() - uploadStartTime;
      onUpdate({
        ...fileUpload,
        progress: 100,
        emitMetrics: true,
        uploadLatency: elapsedMillis,
        completed: true,
      });
    })
    .catch((error) => {
      onUpdate({
        ...fileUpload,
        emitMetrics: true,
        errorOccurred: true,
        errorMessage: `Single part upload error, response ${error}`,
      });
    });
};

/**
 * @description
 * helper method which executes a multipart upload and returns part numbers & etags
 */
export const uploadParts = async (
  multipartUploadUrls: MultipartUploadUrls[],
  axiosInstance: AxiosInstance,
  axiosConfig: AxiosRequestConfig,
  fileUpload: FileUpload,
) => {
  // Sort by part numbers
  multipartUploadUrls.sort((a, b) => (a.partNumber > b.partNumber ? 1 : -1));

  const uploadParts: MultipartUploadPart[] = [];
  for (const multipartUploadUrl of multipartUploadUrls) {
    const url = multipartUploadUrl.url;
    const partNumber = multipartUploadUrl.partNumber;

    const start = (partNumber - 1) * MULTIPART_CHUNK_SIZE;
    const end = partNumber * MULTIPART_CHUNK_SIZE;
    const blob =
      end < fileUpload.file.size ? fileUpload.file.slice(start, end) : fileUpload.file.slice(start);

    // Promises will be sorted by part number as multipartUploadUrls was pre-sorted
    await axiosInstance
      .put(url, blob, axiosConfig)
      .then((response) => {
        uploadParts.push({
          partNumber: partNumber,
          etag: response.headers.etag,
        });
      })
      .catch((error) => {
        throw new Error(`Multipart upload error, response ${error}`);
      });
  }
  return uploadParts;
};

// Minimum file size above which to trigger a multipart upload
export const MULTIPART_THRESHOLD_FILE_SIZE = 16_000_000;
// Size of each file chunk to upload, see default chunk size from https://docs.aws.amazon.com/cli/latest/topic/s3-config.html
export const MULTIPART_CHUNK_SIZE = 16_000_000;
// Axios content type defaults to application/x-www-form-urlencoded
export const DEFAULT_CONTENT_TYPE = 'application/x-www-form-urlencoded';

const multipartUpload = async (
  fileUpload: FileUpload,
  apolloClient: ApolloClient<unknown>,
  axiosInstance: AxiosInstance,
  axiosConfig: AxiosRequestConfig,
  onUpdate: (fileUpload: FileUpload) => void,
) => {
  const uploadStartTime = Date.now();
  const initiateMultipartUploadResult = await initiateUpload(
    apolloClient,
    ADMIN_CLIENT_UPLOAD_BUCKET,
    fileUpload.s3Key,
    fileUpload.file.type ?? DEFAULT_CONTENT_TYPE,
  );

  const uploadToken =
    initiateMultipartUploadResult.data?.multipartUploads?.initiateMultipartUpload?.uploadToken;
  if (initiateMultipartUploadResult.errors || !uploadToken) {
    onUpdate({
      ...fileUpload,
      emitMetrics: true,
      errorOccurred: true,
      errorMessage: `Multipart Upload error for ${uploadToken}, failed to initiate upload: ${JSON.stringify(
        initiateMultipartUploadResult.errors ?? 'uploadToken not included in response',
      )}`,
    });
    return;
  }

  try {
    const partsCount = Math.ceil(fileUpload.file.size / MULTIPART_CHUNK_SIZE);
    const generateMultipartUploadUrlsResult = await generateUploadUrls(
      apolloClient,
      uploadToken,
      partsCount,
    );

    const multipartUploadUrls =
      generateMultipartUploadUrlsResult.data?.multipartUploads?.generateMultipartUploadUrls
        ?.multipartUploadUrls;
    if (generateMultipartUploadUrlsResult.errors || !multipartUploadUrls) {
      throw new Error(
        `Multipart Upload error for ${uploadToken}, failed to generate presigned URLs: ${JSON.stringify(
          generateMultipartUploadUrlsResult.errors ?? 'URLs not included in response',
        )}`,
      );
    }

    const multipartUploadParts = await uploadParts(
      multipartUploadUrls,
      axiosInstance,
      axiosConfig,
      fileUpload,
    );

    const completeMultipartUploadResult = await completeUpload(
      apolloClient,
      uploadToken,
      multipartUploadParts,
    );
    if (completeMultipartUploadResult.errors) {
      throw new Error(
        `Multipart Upload error for ${uploadToken}, failed to complete multipart upload: ${JSON.stringify(
          completeMultipartUploadResult.errors,
        )}`,
      );
    }

    const elapsedMillis = Date.now() - uploadStartTime;
    onUpdate({
      ...fileUpload,
      progress: 100,
      emitMetrics: true,
      errorOccurred: false,
      uploadLatency: elapsedMillis,
      completed: true,
    });
  } catch (error: any) {
    const abortMultipartUploadResult = await abortUpload(apolloClient, uploadToken);
    fileUpload.emitMetrics = true;
    fileUpload.errorOccurred = true;
    fileUpload.errorMessage =
      (error ? error : `Multipart Upload error for ${uploadToken}`) +
      (error?.response?.status ? `\nStatus code: ${error.response.status}` : '') +
      (error?.response?.data ? `\nBody: ${error.response.data}` : '') +
      (abortMultipartUploadResult.errors
        ? `\nFailed to abort upload! ${JSON.stringify(abortMultipartUploadResult.errors)}`
        : '');
    onUpdate({
      ...fileUpload,
    });
  }
};

/**
 * @description
 * Top-level helper method which orchestrates & executes file upload based on input params
 */
export const uploadFileToS3 = async (
  fileUpload: FileUpload,
  apolloClient: ApolloClient<unknown>,
  useMultipartUpload: boolean,
  onUpdate: (fileUpload: FileUpload) => void,
  retryOptions?: RetryOptions,
) => {
  const axiosInstance = getAxiosInstance(retryOptions);

  let completedBytes = 0;
  let maxObservedBytes = 0;

  const isMultiPart = useMultipartUpload && fileUpload.file.size > MULTIPART_THRESHOLD_FILE_SIZE;

  const axiosConfig: RawAxiosRequestConfig = {
    onUploadProgress: (progressEvent: AxiosProgressEvent) => {
      if (progressEvent.loaded === progressEvent.total) {
        completedBytes = completedBytes + progressEvent.total;
      }
      maxObservedBytes = Math.max(maxObservedBytes, completedBytes + progressEvent.loaded);
      onUpdate({
        ...fileUpload,
        emitMetrics: false,
        progress: Math.min(Math.round((maxObservedBytes / fileUpload.file.size) * 100), 100),
      });
    },
    headers: {
      'Content-Type': fileUpload.file.type ?? DEFAULT_CONTENT_TYPE,
    },
  };

  if (isMultiPart) {
    fileUpload.multipartUpload = true;
    await multipartUpload(fileUpload, apolloClient, axiosInstance, axiosConfig, onUpdate);
  } else {
    fileUpload.multipartUpload = false;
    await singlePartUpload(fileUpload, apolloClient, axiosInstance, axiosConfig, onUpdate);
  }
};

/**
 * isImageFile takes in a file name and return if the file is with an image extension.
 * @param fileName string of the file name
 * @returns boolean of if file is image
 */
export function isImageFile(fileName: string): boolean {
  const fileType = mime.getType(fileName);
  return fileType ? fileType.startsWith('image/') : false;
}

/**
 * return type of a file object.
 */
export function getFileType(file: File): string | undefined {
  if (file.type != '') {
    return file.type;
  }
  const fileType = mime.getType(file.name);
  return fileType || undefined;
}
